import React from "react";
import { View, StyleSheet } from "react-native";
import { useTranslation } from "react-i18next";
import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import {
  ENABLE_CONSUMPTION_BILLING,
  formatUsd,
} from "../../../Constants/billing";

// BillingProgressBar: setup status split into REQUIRED vs RECOMMENDED.
//
//   REQUIRED (hard gate to run a workspace):
//     - Credits balance > 0. One-off recharges via Stripe Checkout need NO
//       saved card, so a saved payment method is NOT required to operate.
//
//   RECOMMENDED (complete your billing profile — not gates):
//     - Billing entity (business name + tax ID + address) → tax-compliant invoices.
//     - Saved payment method → enables auto-recharge (off-session charging).
//       (A saved card only matters if the user wants auto-recharge.)
//     - Auto-recharge + monthly spend cap → ongoing convenience.
//
// The user is "ready to run" as soon as they have credits. The recommended
// steps drive a separate "billing profile" completeness indicator.
const BillingProgressBar = ({
  hasPaymentMethod,
  hasBillingAddress,
  hasCredits = false,
  hasAutoRecharge = false,
  hasSpendCap = false,
  creditsBalanceUsd = 0,
  onAddCredits,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const theme = currentTheme?.theme;

  const styles = generateStyles(theme);

  if (!ENABLE_CONSUMPTION_BILLING) {
    // Legacy (pre-consumption) setup: payment method + billing details, both
    // required. Preserve the original behaviour for that mode.
    const completed = (hasPaymentMethod ? 1 : 0) + (hasBillingAddress ? 1 : 0);
    const pct = (completed / 2) * 100;
    const color =
      completed === 0
        ? theme?.red
        : completed < 2
        ? theme?.orange
        : theme?.green;
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.titleContainer}>
            <AppIcon
              icon={AppAssets.icons.billing}
              size={20}
              color={theme?.primary}
            />
            <AppText style={styles.title}>{t("billing.billingSetup")}</AppText>
          </View>
          <AppText style={[styles.status, { color }]}>
            {completed === 2
              ? t("billing.setupComplete")
              : t("billing.setupRequired")}
          </AppText>
        </View>
        <View style={styles.progressContainer}>
          <View
            style={[
              styles.progressBar,
              { backgroundColor: color, width: `${pct}%` },
            ]}
          />
        </View>
        <View style={styles.detailsContainer}>
          {[
            { done: hasPaymentMethod, label: t("billing.paymentMethod") },
            { done: hasBillingAddress, label: t("billing.billingDetails") },
          ].map((s) => (
            <View key={s.label} style={styles.detailItem}>
              <AppText
                leftIcon={
                  s.done ? AppAssets.icons.check : AppAssets.icons.uncheck
                }
                size={16}
                iconSize={14}
                color={s.done ? theme?.green : theme?.red}
                style={[
                  styles.detailText,
                  { color: s.done ? theme?.font_color : theme?.red },
                ]}
              >
                {s.label}
              </AppText>
            </View>
          ))}
        </View>
      </View>
    );
  }

  // Consumption model.
  const recommendedSteps = [
    { done: hasBillingAddress, label: t("billing.billingDetails") },
    { done: hasPaymentMethod, label: t("billing.savedCard") },
    { done: hasAutoRecharge, label: t("billing.autoRecharge") },
    { done: hasSpendCap, label: t("billing.spendCap") },
  ];
  const recommendedDone = recommendedSteps.filter((s) => s.done).length;
  const profileComplete = recommendedDone === recommendedSteps.length;
  const nextStep = recommendedSteps.find((s) => !s.done);

  const readyColor = hasCredits ? theme?.green : theme?.red;
  const profileColor = profileComplete
    ? theme?.green
    : recommendedDone > 0
    ? theme?.orange
    : theme?.font_color;

  return (
    <View style={styles.container}>
      {/* REQUIRED: ready to run */}
      <View style={styles.sectionHeader}>
        <View style={styles.titleContainer}>
          <AppIcon
            icon={hasCredits ? AppAssets.icons.check : AppAssets.icons.risk}
            color={readyColor}
            size={20}
          />
          <AppText style={[styles.title, { color: readyColor }]}>
            {hasCredits
              ? t("billing.readyToRun", { defaultValue: "Ready to run" })
              : t("billing.creditsRequired", {
                  defaultValue: "Credits required",
                })}
          </AppText>
        </View>
        {hasCredits ? (
          <AppText style={[styles.status, { color: readyColor }]}>
            {`${formatUsd(creditsBalanceUsd)} · ${t("billing.ready", {
              defaultValue: "ready",
            })}`}
          </AppText>
        ) : (
          <AppText
            isClickable
            onPress={onAddCredits}
            style={[
              styles.status,
              styles.addCreditsLink,
              { color: readyColor },
            ]}
          >
            {t("billing.addCreditsToStart", {
              defaultValue: "Add credits to start",
            })}
          </AppText>
        )}
      </View>

      {/* RECOMMENDED: billing profile completeness */}
      <View style={[styles.divider, { borderColor: theme?.border_color }]} />
      <View style={styles.sectionHeader}>
        <View style={styles.titleContainer}>
          <AppIcon
            icon={AppAssets.icons.billingDetails}
            size={18}
            color={theme?.primary}
          />
          <AppText style={[styles.title, { fontSize: 14 }]}>
            {t("billing.billingProfile", { defaultValue: "Billing profile" })}
          </AppText>
        </View>
        <AppText style={[styles.status, { color: profileColor, fontSize: 13 }]}>
          {profileComplete
            ? t("billing.profileComplete", { defaultValue: "Complete" })
            : t("billing.profileNextStep", {
                defaultValue: "Next: {{step}}",
                step: nextStep?.label,
              })}
        </AppText>
      </View>
      <View style={styles.progressContainer}>
        <View
          style={[
            styles.progressBar,
            {
              backgroundColor: profileColor,
              width: `${(recommendedDone / recommendedSteps.length) * 100}%`,
            },
          ]}
        />
      </View>
      <View style={styles.recommendedList}>
        {recommendedSteps.map((s) => (
          <View key={s.label} style={styles.recommendedItem}>
            <AppIcon
              icon={s.done ? AppAssets.icons.check : AppAssets.icons.uncheck}
              size={13}
              color={s.done ? theme?.green : theme?.secondary_font_color}
            />
            <AppText
              style={[
                styles.recommendedText,
                {
                  color: s.done
                    ? theme?.font_color
                    : theme?.secondary_font_color,
                },
              ]}
            >
              {s.label}
            </AppText>
          </View>
        ))}
      </View>
    </View>
  );
};

const generateStyles = (theme) =>
  StyleSheet.create({
    container: {
      padding: 20,
      marginBottom: 20,
      marginRight: 20,
      marginTop: 10,
      marginLeft: 10,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: theme?.border_color,
      backgroundColor: theme?.transparent,
      shadowOpacity: 0.1,
      shadowRadius: 3.84,
      elevation: 5,
    },
    sectionHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 15,
    },
    titleContainer: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      flexWrap: "wrap",
    },
    title: {
      fontSize: 16,
      fontWeight: "600",
      marginLeft: 8,
      color: theme?.font_color,
    },
    status: {
      fontSize: 14,
      fontWeight: "500",
      color: theme?.font_color,
    },
    addCreditsLink: {
      textDecorationLine: "underline",
      cursor: "pointer",
      fontWeight: "bold",
    },
    divider: {
      borderBottomWidth: 1,
      marginVertical: 12,
    },
    progressContainer: {
      height: 8,
      backgroundColor: theme?.lightGrey,
      borderRadius: 4,
      overflow: "hidden",
      marginBottom: 12,
    },
    progressBar: {
      height: "100%",
      borderRadius: 4,
      transition: "width 0.3s ease-in-out",
    },
    detailsContainer: {
      flexDirection: "row",
      justifyContent: "space-around",
    },
    detailItem: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      justifyContent: "center",
    },
    detailText: {
      fontSize: 12,
      marginLeft: 6,
      fontWeight: "bold",
    },
    recommendedList: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 10,
    },
    recommendedItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
    },
    recommendedText: {
      fontSize: 13,
    },
  });

export default BillingProgressBar;
